// src/pages/user/Profile.js
// Maxula
// Correctif 2FA : champs de code durcis (anti-autofill, 6 chiffres only).
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FiCamera, FiUpload, FiCheck } from 'react-icons/fi';
import { userAPI, authAPI } from '../../services/api';
import { normalizeImagePath } from '../../utils/imageUtils';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import Loading from '../../components/common/Loading';
import PushToggle from '../../components/PushToggle';

const phoneIsValid = (phone) => {
  if (!phone) return true; // optional
  // accept formats like 06XXXXXXXX or +2126XXXXXXXX
  return /^(\+212|0)?6\d{8}$/.test(phone);
};

const Profile = () => {
  const { user, setUser, refreshUser } = useAuth() || {}; // try to use setUser if provided by context
  const mountedRef = useRef(true);

  const [loading, setLoading] = useState(false);       // saving state
  const [initialLoading, setInitialLoading] = useState(true); // initial load state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    whatsappOptIn: true,
    smsOptIn: false,
    alertsOptOut: false
  });

  // ===== P3-A : double authentification =====
  const [twoFASetup, setTwoFASetup] = useState(null);   // { otpAuthUrl, manualKey }
  const [twoFAToken, setTwoFAToken] = useState('');
  const [backupCodes, setBackupCodes] = useState(null);  // affichés UNE fois
  const [twoFABusy, setTwoFABusy] = useState(false);
  const [disableToken, setDisableToken] = useState('');
  const start2FA = async () => {
    setTwoFABusy(true);
    try {
      const r = await authAPI.setup2FA();
      setTwoFASetup(r?.data?.data || null);
      setTwoFAToken('');
    } catch (e) { toast.error(e?.response?.data?.message || 'Erreur'); }
    finally { setTwoFABusy(false); }
  };
  const activate2FA = async () => {
    setTwoFABusy(true);
    try {
      const r = await authAPI.verify2FA(twoFAToken.trim());
      setBackupCodes(r?.data?.data?.backupCodes || []);
      setTwoFASetup(null);
      toast.success('Double authentification activée !');
      if (typeof refreshUser === 'function') refreshUser();
    } catch (e) { toast.error(e?.response?.data?.message || 'Code invalide'); }
    finally { setTwoFABusy(false); }
  };
  const deactivate2FA = async () => {
    setTwoFABusy(true);
    try {
      await authAPI.disable2FA(disableToken.trim());
      setDisableToken('');
      toast.success('Double authentification désactivée');
      if (typeof refreshUser === 'function') refreshUser();
    } catch (e) { toast.error(e?.response?.data?.message || 'Code invalide'); }
    finally { setTwoFABusy(false); }
  };

  // Fill form from context user if available, otherwise fetch latest profile
  const loadProfileFromContext = useCallback(() => {
    if (user) {
      setFormData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        phone: user.phone || '',
        whatsappOptIn: user.whatsappOptIn !== false,
        smsOptIn: user.smsOptIn === true,
        alertsOptOut: user.alertsOptOut === true
      });
      setInitialLoading(false);
      return true;
    }
    return false;
  }, [user]);

  const fetchProfile = useCallback(async () => {
    setInitialLoading(true);
    try {
      const res = await userAPI.getProfile?.(); // if available
      const payload = res?.data ?? res ?? {};
      const profile = payload?.data ?? payload ?? {};
      if (!mountedRef.current) return;
      setFormData({
        firstName: profile.firstName || '',
        lastName: profile.lastName || '',
        email: profile.email || '',
        phone: profile.phone || ''
      });
    } catch (err) {
      console.error('Erreur chargement profil:', err);
      toast.warn('Impossible de récupérer les informations de profil (utilisation des données du contexte si disponibles).');
      loadProfileFromContext();
    } finally {
      if (mountedRef.current) setInitialLoading(false);
    }
  }, [loadProfileFromContext]);

  useEffect(() => {
    // prefer context user, otherwise fetch from API
    const usedContext = loadProfileFromContext();
    if (!usedContext) {
      fetchProfile();
    } else {
      setInitialLoading(false);
    }

    return () => {
      mountedRef.current = false;
    };
  }, [fetchProfile, loadProfileFromContext]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // ===== Photo de profil (avatar) =====
  const avatarInputRef = useRef(null);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [avatarFile, setAvatarFile] = useState(null);

  const currentAvatar = avatarPreview || normalizeImagePath(user?.avatar) || '';
  const avatarInitials =
    `${(formData.firstName || user?.firstName || '').charAt(0)}${(formData.lastName || user?.lastName || '').charAt(0)}`
      .toUpperCase() || 'U';

  const onPickAvatar = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/^image\//.test(file.type)) { toast.error('Veuillez choisir une image'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Image trop lourde (5 Mo max)'); return; }
    if (avatarPreview) URL.revokeObjectURL(avatarPreview);
    setAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file));
  };

  const uploadAvatar = async () => {
    if (!avatarFile) return;
    setAvatarBusy(true);
    try {
      const fd = new FormData();
      fd.append('avatar', avatarFile);
      const res = await userAPI.updateMedia(fd);
      const newAvatar = res?.data?.data?.avatar;
      if (typeof setUser === 'function' && newAvatar) {
        try { setUser({ ...(user || {}), avatar: newAvatar }); } catch (err) {}
      } else if (typeof refreshUser === 'function') {
        refreshUser();
      }
      toast.success('Photo de profil mise à jour');
      setAvatarFile(null);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Erreur lors de l'envoi de la photo");
    } finally {
      if (mountedRef.current) setAvatarBusy(false);
    }
  };

  const cancelAvatar = () => {
    if (avatarPreview) URL.revokeObjectURL(avatarPreview);
    setAvatarPreview(null);
    setAvatarFile(null);
    if (avatarInputRef.current) avatarInputRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // client-side validation
    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      toast.error('Prénom et nom sont requis');
      return;
    }

    if (!phoneIsValid(formData.phone)) {
      toast.error('Format de téléphone incorrect (ex: 06XXXXXXXX ou +2126XXXXXXXX)');
      return;
    }

    setLoading(true);
    try {
      const res = await userAPI.updateProfile(formData);
      // Accept multiple shapes: res.data.data | res.data | res
      const payload = res?.data?.data ?? res?.data ?? res ?? {};
      // update local UI
      if (mountedRef.current) {
        // try to sync auth context if setUser exists
        if (typeof setUser === 'function') {
          const updatedUser = {
            ...(user || {}),
            ...payload,
            firstName: payload.firstName ?? formData.firstName,
            lastName: payload.lastName ?? formData.lastName,
            phone: payload.phone ?? formData.phone
          };
          try { setUser(updatedUser); } catch (err) { /* ignore if not supported */ }
        }
        setFormData(prev => ({
          ...prev,
          firstName: payload.firstName ?? prev.firstName,
          lastName: payload.lastName ?? prev.lastName,
          phone: payload.phone ?? prev.phone
        }));
      }

      toast.success('Profil mis à jour avec succès');
    } catch (error) {
      console.error('Erreur updateProfile:', error);
      // try to show backend message
      const msg = error?.response?.data?.message || error?.message || 'Erreur lors de la mise à jour';
      toast.error(msg);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  };

  if (initialLoading) return <Loading />;

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-2xl font-bold mb-6">Informations personnelles</h2>

      {/* Photo de profil */}
      <div className="flex items-center gap-4 mb-6 pb-6 border-b border-gray-100">
        <div className="relative shrink-0">
          {currentAvatar ? (
            <img
              src={currentAvatar}
              alt="Avatar"
              className="w-20 h-20 rounded-full object-cover border border-gray-200 bg-gray-100"
            />
          ) : (
            <div
              className="w-20 h-20 rounded-full flex items-center justify-center text-xl font-semibold"
              style={{ backgroundColor: '#FCE7F1', color: '#c71a6e' }}
            >
              {avatarInitials}
            </div>
          )}
          <button
            type="button"
            onClick={() => avatarInputRef.current?.click()}
            className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-[#e11d7b] text-white flex items-center justify-center shadow hover:opacity-90"
            aria-label="Changer la photo"
            title="Changer la photo"
          >
            <FiCamera size={14} />
          </button>
        </div>

        <div className="min-w-0">
          <p className="font-medium text-gray-800">Photo de profil</p>
          <p className="text-xs text-gray-500 mb-2">JPG, PNG ou WebP — 5 Mo max.</p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => avatarInputRef.current?.click()}
              className="text-sm px-3 py-1.5 rounded-lg border border-gray-300 hover:bg-gray-50 inline-flex items-center gap-1.5"
            >
              <FiUpload size={14} /> Choisir une image
            </button>
            {avatarFile && (
              <>
                <button
                  type="button"
                  onClick={uploadAvatar}
                  disabled={avatarBusy}
                  className="text-sm px-3 py-1.5 rounded-lg bg-[#e11d7b] text-white hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-1.5"
                >
                  <FiCheck size={14} /> {avatarBusy ? 'Envoi…' : 'Enregistrer la photo'}
                </button>
                <button
                  type="button"
                  onClick={cancelAvatar}
                  disabled={avatarBusy}
                  className="text-sm px-3 py-1.5 rounded-lg text-gray-600 hover:bg-gray-100"
                >
                  Annuler
                </button>
              </>
            )}
          </div>
          <input
            ref={avatarInputRef}
            type="file"
            accept="image/*"
            onChange={onPickAvatar}
            className="hidden"
          />
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-gray-700 font-medium mb-2">Prénom</label>
            <input
              type="text"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              autoComplete="given-name"
            />
          </div>

          <div>
            <label className="block text-gray-700 font-medium mb-2">Nom</label>
            <input
              type="text"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              autoComplete="family-name"
            />
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-gray-700 font-medium mb-2">Email</label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            disabled
            className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
          />
          <p className="text-sm text-gray-500 mt-1">L'email ne peut pas être modifié</p>
        </div>

        <div className="mb-6">
          <label className="block text-gray-700 font-medium mb-2">Téléphone</label>
          <input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder="06XXXXXXXX"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            autoComplete="tel"
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className="bg-primary-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary-700 disabled:opacity-50 flex items-center"
        >
          {loading ? (
            <svg className="animate-spin h-5 w-5 mr-3 text-white" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16 8 8 0 01-8-8z" />
            </svg>
          ) : null}
          {loading ? 'Mise à jour...' : 'Enregistrer les modifications'}
        </button>

        {/* P1-A : préférences de notifications transactionnelles */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <h2 className="font-semibold text-gray-900 mb-1">Notifications</h2>
          <p className="text-sm text-gray-600 mb-4">Recevez les confirmations de commande, coupons et remboursements directement sur votre téléphone.</p>
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-4 rounded-xl border border-gray-200 px-4 py-3">
              <span>
                <span className="block font-medium text-sm">Notifications push (navigateur / app)</span>
                <span className="block text-xs text-gray-500">Alertes même quand le site est fermé — installez l'app pour en profiter au mieux</span>
              </span>
              <PushToggle />
            </div>
            <label className="flex items-center justify-between gap-4 rounded-xl border border-gray-200 px-4 py-3 cursor-pointer">
              <span>
                <span className="block font-medium text-sm">Notifications WhatsApp</span>
                <span className="block text-xs text-gray-500">Canal recommandé au Maroc</span>
              </span>
              <input type="checkbox" className="h-5 w-5 accent-pink-600"
                checked={formData.whatsappOptIn !== false}
                onChange={(e) => setFormData((f) => ({ ...f, whatsappOptIn: e.target.checked }))} />
            </label>
            <label className="flex items-center justify-between gap-4 rounded-xl border border-gray-200 px-4 py-3 cursor-pointer">
              <span>
                <span className="block font-medium text-sm">Alertes sur mes favoris</span>
                <span className="block text-xs text-gray-500">Offre qui expire bientôt, baisse de prix, rappels de commande</span>
              </span>
              <input type="checkbox" className="h-5 w-5 accent-pink-600"
                checked={formData.alertsOptOut !== true}
                onChange={(e) => setFormData((f) => ({ ...f, alertsOptOut: !e.target.checked }))} />
            </label>
            <label className="flex items-center justify-between gap-4 rounded-xl border border-gray-200 px-4 py-3 cursor-pointer">
              <span>
                <span className="block font-medium text-sm">SMS de secours</span>
                <span className="block text-xs text-gray-500">Utilisé uniquement si WhatsApp échoue</span>
              </span>
              <input type="checkbox" className="h-5 w-5 accent-pink-600"
                checked={formData.smsOptIn === true}
                onChange={(e) => setFormData((f) => ({ ...f, smsOptIn: e.target.checked }))} />
            </label>
          </div>
        </div>

        {/* P3-A : sécurité — double authentification */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <h2 className="font-semibold text-gray-900 mb-1">Sécurité — Double authentification</h2>
          <p className="text-sm text-gray-600 mb-4">Protégez votre compte avec un code à 6 chiffres (Google Authenticator, Authy...).</p>

          {user && user.twoFAEnabled ? (
            <div className="rounded-xl border border-green-200 bg-green-50 p-4">
              <p className="text-sm text-green-800 font-medium mb-3">La double authentification est ACTIVÉE sur votre compte.</p>
              <div className="flex flex-col sm:flex-row gap-2">
                <input value={disableToken}
                  onChange={(e) => setDisableToken(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="Code à 6 chiffres pour désactiver" inputMode="numeric" maxLength={6}
                  autoComplete="off" name="maxula-totp-disable" autoCorrect="off" spellCheck={false}
                  className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200" />
                <button type="button" disabled={twoFABusy || disableToken.length !== 6} onClick={deactivate2FA}
                  className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-50">
                  {twoFABusy ? '...' : 'Désactiver'}
                </button>
              </div>
            </div>
          ) : twoFASetup ? (
            <div className="rounded-xl border border-gray-200 p-4">
              <p className="text-sm text-gray-700 mb-3"><strong>1.</strong> Scannez ce QR code avec votre application :</p>
              <div className="flex justify-center mb-3 bg-white p-3 rounded-lg border border-gray-100 w-fit mx-auto">
                <QRCodeSVG value={twoFASetup.otpAuthUrl || ''} size={168} />
              </div>
              <p className="text-xs text-gray-500 text-center mb-4">
                Ou saisissez la clé manuellement : <span className="font-mono font-semibold select-all">{twoFASetup.manualKey}</span>
              </p>
              <p className="text-sm text-gray-700 mb-2"><strong>2.</strong> Entrez le code affiché par l'application :</p>
              <div className="flex flex-col sm:flex-row gap-2">
                <input value={twoFAToken}
                  onChange={(e) => setTwoFAToken(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="123 456" inputMode="numeric" maxLength={6}
                  autoComplete="off" name="maxula-totp" autoCorrect="off" spellCheck={false}
                  className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm text-center font-mono tracking-widest focus:outline-none focus:ring-2 focus:ring-primary-200" />
                <button type="button" disabled={twoFABusy || twoFAToken.length !== 6} onClick={activate2FA}
                  className="px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700 disabled:opacity-50">
                  {twoFABusy ? 'Vérification...' : 'Activer'}
                </button>
              </div>
            </div>
          ) : (
            <button type="button" disabled={twoFABusy} onClick={start2FA}
              className="px-4 py-2 rounded-lg bg-gray-900 text-white text-sm font-semibold hover:bg-gray-800 disabled:opacity-50">
              {twoFABusy ? 'Préparation...' : 'Activer la double authentification'}
            </button>
          )}

          {backupCodes && (
            <div className="mt-4 rounded-xl border-2 border-amber-300 bg-amber-50 p-4">
              <p className="text-sm font-bold text-amber-900 mb-1">Codes de secours — affichés UNE SEULE FOIS</p>
              <p className="text-xs text-amber-800 mb-3">Conservez-les en lieu sûr : chaque code permet une connexion si vous perdez votre téléphone (usage unique).</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
                {backupCodes.map((c) => (
                  <span key={c} className="font-mono text-sm bg-white border border-amber-200 rounded px-2 py-1 text-center select-all">{c}</span>
                ))}
              </div>
              <button type="button"
                onClick={() => { navigator.clipboard?.writeText(backupCodes.join('\n')); toast.success('Codes copiés'); }}
                className="text-xs font-semibold text-amber-900 underline">Copier tous les codes</button>
            </div>
          )}
        </div>
      </form>
    </div>
  );
};

export default Profile;